<script>
    import { createEventDispatcher } from 'svelte';
    import { isLoading } from '../stores/chatStore.js';
    
    const dispatch = createEventDispatcher();
    
    let inputText = '';
    let textareaElement;
    
    // Handle form submission
    function handleSubmit(event) {
        event.preventDefault();
        sendMessage();
    }
    
    // Send message
    function sendMessage() {
        if (!inputText.trim() || $isLoading) return;
        
        dispatch('send', {
            message: inputText.trim()
        });
        
        inputText = '';
        adjustTextareaHeight();
    }
    
    // Handle keyboard shortcuts
    function handleKeydown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    }
    
    // Auto-resize textarea
    function adjustTextareaHeight() {
        if (textareaElement) {
            textareaElement.style.height = 'auto';
            textareaElement.style.height = Math.min(textareaElement.scrollHeight, 120) + 'px';
        }
    }
    
    // Handle input changes
    function handleInput() {
        adjustTextareaHeight();
    }
    
    // Focus input when component mounts
    import { onMount } from 'svelte';
    onMount(() => {
        if (textareaElement) {
            textareaElement.focus();
        }
    });
</script>

<form class="message-input-form" on:submit={handleSubmit}>
    <div class="input-container">
        <textarea
            bind:this={textareaElement}
            bind:value={inputText}
            on:keydown={handleKeydown}
            on:input={handleInput}
            placeholder="Nachricht eingeben... (Enter zum Senden, Shift+Enter für neue Zeile)"
            disabled={$isLoading}
            rows="1"
            class="message-input"
        ></textarea>
        
        <button
            type="submit"
            class="send-button"
            disabled={!inputText.trim() || $isLoading}
            title="Nachricht senden"
            aria-label="Nachricht senden"
        >
            {#if $isLoading}
                <div class="loading-spinner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                </div>
            {:else}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                </svg>
            {/if}
        </button>
    </div>
    
    <div class="input-hint">
        <span class="hint-text">
            {#if $isLoading}
                KI antwortet...
            {:else}
                Enter zum Senden • Shift+Enter für neue Zeile
            {/if}
        </span>
    </div>
</form>

<style>
    .message-input-form {
        background: #2d2d2d;
        border-top: 1px solid #404040;
        padding: 16px 20px 12px;
    }
    
    .input-container {
        display: flex;
        align-items: flex-end;
        gap: 12px;
        background: #1a1a1a;
        border: 1px solid #404040;
        border-radius: 24px;
        padding: 12px 16px;
        transition: border-color 0.2s ease;
    }
    
    .input-container:focus-within {
        border-color: #4a9eff;
        box-shadow: 0 0 0 2px rgba(74, 158, 255, 0.1);
    }
    
    .message-input {
        flex: 1;
        background: none;
        border: none;
        color: #ffffff;
        font-size: 1rem;
        line-height: 1.5;
        resize: none;
        outline: none;
        min-height: 24px;
        max-height: 120px;
        overflow-y: auto;
        font-family: inherit;
    }
    
    .message-input::placeholder {
        color: #888;
    }
    
    .message-input:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    
    .send-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: #4a9eff;
        border: none;
        border-radius: 50%;
        color: white;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;
    }
    
    .send-button:hover:not(:disabled) {
        background: #0066cc;
        transform: scale(1.05);
    }
    
    .send-button:active:not(:disabled) {
        transform: scale(0.95);
    }
    
    .send-button:disabled {
        background: #555;
        cursor: not-allowed;
        transform: none;
    }
    
    .loading-spinner {
        animation: spin 1s linear infinite;
    }
    
    .input-hint {
        display: flex;
        justify-content: center;
        margin-top: 8px;
    }
    
    .hint-text {
        font-size: 0.75rem;
        color: #888;
        text-align: center;
    }
    
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
    
    @media (max-width: 768px) {
        .message-input-form {
            padding: 12px 16px 8px;
        }
        
        .input-container {
            padding: 10px 14px;
        }
        
        .message-input {
            font-size: 16px; /* Prevents zoom on iOS */
        }
        
        .message-input::placeholder {
            font-size: 14px;
        }
        
        .send-button {
            width: 36px;
            height: 36px;
        }
        
        .hint-text {
            font-size: 0.7rem;
        }
    }
    
    /* Custom scrollbar for textarea */
    .message-input::-webkit-scrollbar {
        width: 4px;
    }
    
    .message-input::-webkit-scrollbar-track {
        background: transparent;
    }
    
    .message-input::-webkit-scrollbar-thumb {
        background: #555;
        border-radius: 2px;
    }
    
    .message-input::-webkit-scrollbar-thumb:hover {
        background: #777;
    }
</style>
